import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User } from 'lucide-react';

// The fetchBotResponse function is self-contained for now and can be part of this component.
// If it needed access to app-level state or Firebase services not directly related to chat display,
// it might be passed as a prop or have those services passed to it.
const fetchBotResponse = async (userMessage, setMessages, setIsTyping) => {
  setIsTyping(true);
  
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // In a real application, you would fetch from your API here
  // const response = await fetch('/api/chat', {
  //   method: 'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body: JSON.stringify({ message: userMessage })
  // });
  // const data = await response.json();
  
  const botResponses = [
    "I understand you're saying: " + userMessage,
    "That's interesting! Tell me more.",
    "How can I assist you with that?",
    "Could you provide more details?",
    "I'm processing that information."
  ];
  
  const randomResponse = botResponses[Math.floor(Math.random() * botResponses.length)];
  
  setMessages(prev => [...prev, { role: 'bot', content: randomResponse }]);
  setIsTyping(false);
};

const ChatInterface = ({ messages, setMessages, activeView }) => {
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat view becomes active
  useEffect(() => {
    if (activeView === 'chat') {
      inputRef.current?.focus();
    }
  }, [activeView]);

  const handleSubmit = () => {
    if (input.trim() === '') return;
    
    const userMessage = input.trim();
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setInput('');
    
    fetchBotResponse(userMessage, setMessages, setIsTyping);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {messages.map((message, index) => (
          <div 
            key={index} 
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div 
              className={`max-w-xs md:max-w-md lg:max-w-lg rounded-lg p-3 ${
                message.role === 'user' 
                  ? 'bg-blue-500 text-white rounded-br-none' 
                  : 'bg-white text-gray-800 rounded-bl-none shadow'
              }`}
            >
              <div className="flex items-center space-x-2 mb-1">
                {message.role === 'bot' ? (
                  <Bot size={16} className="text-blue-500" />
                ) : (
                  <User size={16} className="text-white" />
                )}
                <span className="font-medium">
                  {message.role === 'bot' ? 'Chatbot' : 'You'}
                </span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white text-gray-800 rounded-lg rounded-bl-none p-3 shadow max-w-xs">
              <div className="flex items-center space-x-2 mb-1">
                <Bot size={16} className="text-blue-500" />
                <span className="font-medium">Chatbot</span>
              </div>
              <div className="flex space-x-1 items-center">
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"></div>
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input Area */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            className="flex-grow px-4 py-2 focus:outline-none"
            disabled={isTyping} // Optionally disable input while bot is typing
          />
          <button 
            onClick={handleSubmit} 
            className="bg-blue-500 text-white p-2 px-4 hover:bg-blue-600 transition-colors"
            disabled={isTyping || input.trim() === ''}
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface; 